import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, HelpCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse, DomainRenewal } from '../types';
import { registrarName } from '../lib/registrar-names';

/**
 * The domain's latest renewal attempt, for people who can renew. Shows an
 * `unknown` outcome prominently — it may have been charged and blocks further
 * renewals — with buttons to resolve it after checking the registrar.
 * Recent successes and failures show as a one-line note.
 */
export default function RenewalStatus({ domainId, refreshKey, onChanged }: { domainId: string; refreshKey?: number; onChanged?: () => void }) {
  const { fetchApi } = useApi();
  const [latest, setLatest] = useState<DomainRenewal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An unresolved unknown renewal wins over anything newer: it may have been
  // charged and it blocks further renewals until someone resolves it.
  const load = useCallback(async () => {
    try {
      const base = `/renewals?domain_id=${encodeURIComponent(domainId)}&limit=1`;
      const unknown = await fetchApi<ApiResponse<DomainRenewal[]>>(`${base}&status=unknown`);
      if (unknown.data?.[0]) { setLatest(unknown.data[0]); return; }
      const res = await fetchApi<ApiResponse<DomainRenewal[]>>(`${base}&status=succeeded,failed`);
      setLatest(res.data?.[0] ?? null);
    } catch { /* non-fatal */ }
  }, [fetchApi, domainId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const resolve = async (outcome: 'succeeded' | 'failed') => {
    if (!latest) return;
    setBusy(true); setError(null);
    try {
      await fetchApi<ApiResponse<DomainRenewal>>(`/renewals/${latest.id}/resolve`, { method: 'POST', body: JSON.stringify({ outcome }) });
      await load(); // another unknown may still be pending
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not resolve');
    } finally {
      setBusy(false);
    }
  };

  if (!latest) return null;
  const when = latest.completed_at ? formatDistanceToNow(new Date(latest.completed_at), { addSuffix: true }) : '';
  const registrar = registrarName(latest.registrar_type);

  if (latest.status === 'unknown') {
    return (
      <div className="px-6 py-3 bg-orange-50 border-t border-orange-200 text-sm text-orange-900">
        <div className="flex items-start gap-2">
          <HelpCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div><strong>Renewal outcome unknown</strong> ({when}). {latest.message}</div>
            <div className="text-xs">Check the domain at {registrar}. Further renewals of this domain are blocked until this is resolved.</div>
            <div className="flex gap-2">
              <button disabled={busy} onClick={() => resolve('succeeded')} className="btn btn-secondary py-1 text-xs">It renewed</button>
              <button disabled={busy} onClick={() => resolve('failed')} className="btn btn-secondary py-1 text-xs">It didn't renew</button>
            </div>
            {error && <div className="text-xs text-red-600">{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Only surface recent outcomes; older history lives in the audit log.
  if (!latest.completed_at || Date.now() - new Date(latest.completed_at).getTime() > 30 * 24 * 3600 * 1000) return null;
  return (
    <div className="px-6 py-2.5 border-t border-slate-100 text-xs text-slate-500 flex items-start gap-1.5">
      {latest.status === 'succeeded'
        ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-px shrink-0" /> Renewed {latest.years > 1 ? `for ${latest.years} years ` : ''}at {registrar} {when}{latest.charged_amount != null ? ` for ${latest.charged_amount.toFixed(2)} ${latest.currency ?? ''}` : ''}{latest.order_id ? ` · order ${latest.order_id}` : ''}.</>
        : <><XCircle className="h-3.5 w-3.5 text-red-500 mt-px shrink-0" /> Last renewal attempt {when} didn't go through: {latest.message}</>}
    </div>
  );
}
